import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import Breadcrumbs from "../components/Breadcrumbs";
import Footer from "../components/Footer";
import { Trash, Plus } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { HAMPER_DISCOUNT } from "../../src/config/discounts";
import AlertModal from "../components/AlertModal";


function Cart() {
  const navigate = useNavigate();
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  const [deliveryCities, setDeliveryCities] = useState([]);
  const [addressError, setAddressError] = useState("");
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const { user, setRedirectAfterAuth, loading } = useAuth();
  const hamperIdRef = useRef(null);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const {
    selectedAccessories,
    selectedFlowers,
    selectedVips,
    removeAccessory,
    removeFlower,
    removeVip,
    hamperItems,
    extras,
    hamperQuantity,
    setHamperQuantity,
    updateHamperQuantity,
    setHamperType,
    setHamperItems,
    validHamper,
    removeHamperById,
    updateFlowerQuantity,
    updateAccessoryQuantity,
    updateVipQuantity,
    useSavedAddress,
    setUseSavedAddress,
    deliveryCity,
    setDeliveryCity,
    deliveryAddress,
    setDeliveryAddress,
    wantsSameDay,
    setWantsSameDay,
    sameDayFee,
    syncDeliveryFee,
    deliveryNote,
    setDeliveryNote,
    giftMessage,
    setGiftMessage,
    clearCart,
    hamperType,
    buildModeRef,
    isAfterCutoff,
  } = useCart();

  useEffect(() => {
    if (!loading && !user) {
      // Save guest cart data
      const guestCart = {
        selectedAccessories,
        selectedFlowers,
        selectedVips,
        hamperItems,
      };
      localStorage.setItem("guestCart", JSON.stringify(guestCart));

      // Keep redirect destination
      setRedirectAfterAuth("/cart");

      // Show alert
      setAlert({
        open: true,
        message: "Please sign in or create an account to continue your purchase.",
        type: "info",
      });

      // Delay navigation to let the user see the alert
      const timer = setTimeout(() => {
        navigate("/signin");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [
    user,
    loading,
    navigate,
    selectedAccessories,
    selectedFlowers,
    selectedVips,
    hamperItems,
    setRedirectAfterAuth,
  ]);


  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/settings`, {
          credentials: "include", // ✅ send cookie
        });
        if (!res.ok) throw new Error("Failed to fetch settings");
        const data = await res.json();
        setSiteSettings(data);
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    };
    fetchSettings();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchDeliveryCities = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/settings`, {
          withCredentials: true, // ✅ send cookie
        });
        setDeliveryCities(res.data.cities || []);
      } catch (err) {
        console.error("Failed to load delivery cities", err);
      }
    };

    fetchDeliveryCities();
  }, [BASE_URL]);


  const hasFlower = selectedFlowers.length > 0;
  const accessoryCount = selectedAccessories.length + selectedVips.length;


  useEffect(() => {
    syncDeliveryFee();
  }, [wantsSameDay, deliveryCity, deliveryAddress, useSavedAddress, syncDeliveryFee]);

  const safePrice = (p) => {
    const num = parseFloat(p);
    return isNaN(num) ? 0 : num;
  };

  const hamperSubtotal = hamperItems && hamperItems.length > 0
  ? hamperItems.reduce(
      (sum, item) => sum + parseFloat(item?.price || 0),
      0
    )
  : 0;

  // ✅ Calculate extras total
    const extrasTotal =
      extras.flowers.reduce((sum, f) => sum + safePrice(f?.price) * (f.quantity || 1), 0) +
      extras.accessories.reduce((sum, a) => sum + safePrice(a?.price) * (a.quantity || 1), 0) +
      extras.vips.reduce((sum, v) => sum + safePrice(v?.price) * (v.quantity || 1), 0);
  
    // ✅ Base hamper calculations
    const hamperSubtotalTotal = hamperSubtotal * hamperQuantity;
    const hamperDiscountRate = HAMPER_DISCOUNT[hamperType] || 0;
    const hamperDiscount = hamperSubtotalTotal * hamperDiscountRate;
  
    // ✅ Subtotal before tax
    const hamperTotal = hamperSubtotalTotal - hamperDiscount;
    const subtotal = hamperTotal + extrasTotal;
  
    // ✅ Tax calculation (6.25%)
    const TAX_RATE = 0.0625;
    const taxAmount = Number((subtotal * TAX_RATE).toFixed(2));
  
    // ✅ Final total including same-day fee and tax
    const totalAmount = subtotal + taxAmount + sameDayFee;

  const handleProceed = () => {
    // Perform extra validation for typed-in address
    if (!useSavedAddress) {
      if (!deliveryCity || !deliveryAddress) {
        setAddressError("Please select a city and enter an address.");
        return;
      }

      if (!deliveryAddress.toLowerCase().includes(deliveryCity.toLowerCase())) {
        setAddressError("Address must include the selected city.");
        return;
      }
    }

    // Clear any existing error before proceeding
    setAddressError("");

    navigate("/checkout", {
      state: {
        hamperItems,
        selectedFlowers,
        selectedAccessories,
        selectedVips,
        hamperType,
        deliveryCity: useSavedAddress ? selectedSavedAddress?.city : deliveryCity,
        deliveryAddress: useSavedAddress ? selectedSavedAddress?.fullAddress : deliveryAddress,
        sameDayFee,
        deliveryNote,
        giftMessage,
      },
    });
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setDeliveryAddress(input);

    if (!deliveryCity) {
      setAddressError("Please select a delivery city first.");
      setIsAddressValid(false);
      return;
    }

    if (!input.toLowerCase().includes(deliveryCity.toLowerCase())) {
      setAddressError(`Address must include the selected city: ${deliveryCity}`);
      setIsAddressValid(false);
    } else {
      setAddressError("");
      setIsAddressValid(true);
    }
  };

  useEffect(() => {
    if (deliveryAddress) {
      if (deliveryCity && !deliveryAddress.toLowerCase().includes(deliveryCity.toLowerCase())) {
        setAddressError(`Address must include the selected city: ${deliveryCity}`);
        setIsAddressValid(false);
      } else {
        setAddressError("");
        setIsAddressValid(true);
      }
    }
  }, [deliveryCity, deliveryAddress]);


  const formatCutoffTime = (time24) => {
    if (!time24) return "";

    const [hourStr, minuteStr] = time24.split(":");
      let hour = parseInt(hourStr, 10);
      const minute = minuteStr.padStart(2, '0');

      let ampm;
      if (hour === 0) {
        hour = 12;
        ampm = "AM";
      } else if (hour === 12) {
        ampm = "PM";
      } else if (hour > 12) {
        hour -= 12;
        ampm = "PM";
      } else {
        ampm = "AM";
      }
    return `${hour}:${minute} ${ampm}`;
  };

  return (
    <>
      <Breadcrumbs />
      <section className="bg-gray-100 py-20 text-center px-4 md:px-6">
        <h1 className="text-4xl font-bold mb-4">Your Cart</h1>
        <p className="text-gray-600 max-w-xl mx-auto">Review your selected items and set delivery details.</p>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 space-y-8">
        {!hasFlower && accessoryCount === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <h2 className="text-2xl font-semibold mb-4">Your cart is empty</h2>
            <p className="mb-6">Browse our shop to add items to your cart.</p>
            <button
              onClick={() => navigate("/shop")}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            >
              Go to Shop
            </button>
          </div>
        ) : (
          <>
            {validHamper && hamperItems.length > 0 && (
              <div className="border border-gray-300 rounded-xl p-6 bg-white shadow-sm">
                <div className="flex justify-between items-start flex-wrap md:flex-nowrap gap-6">
                  <div className="flex-1 space-y-4">
                    <h2 className="text-xl font-bold text-blue-800">
                      Hamper Package: {hamperType}
                    </h2>

                    {/* Render all hamper items */}
                    {hamperItems.map((item) => (
                      <div
                        key={item._id}
                        className={`p-4 rounded border flex justify-between items-center
                          ${
                            item.type === "vip"
                              ? "bg-yellow-50 border-yellow-300"
                              : item.type === "flower"
                              ? "bg-pink-50 border-pink-300"
                              : "bg-blue-50 border-blue-300"
                          }`}
                      >
                        <div>
                          <p className="font-semibold">
                            {item.type === "flower" && "🌸 "}
                            {item.type === "accessory" && "🎁 "}
                            {item.type === "vip" && "💎 "}
                            {item.name}
                          </p>
                          <p
                            className={`text-sm ${
                              item.type === "vip" ? "text-yellow-700" : "text-gray-600"
                            }`}
                          >
                            ${Number(item.price).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="w-full md:w-52 flex flex-col items-center text-center justify-center md:self-center">
                    <p className="text-xl font-semibold mb-2 text-gray-800">
                      ${hamperTotal.toFixed(2)}
                    </p>

                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => updateHamperQuantity(-1)}
                        className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        −
                      </button>
                      <span className="text-sm font-medium">{hamperQuantity}</span>
                      <button
                        onClick={() => updateHamperQuantity(1)}
                        className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        +
                      </button>
                    </div>

                    <div className="text-xs text-gray-600 leading-snug">
                      <p>Subtotal (x{hamperQuantity}): ${hamperSubtotalTotal.toFixed(2)}</p>
                      <p>Discount ({(HAMPER_DISCOUNT[hamperType] * 100).toFixed(0)}%): -${hamperDiscount.toFixed(2)}</p>
                    </div>

                    <button
                      onClick={() =>
                        setPendingRemove({
                          type: "hamper",
                          id: (hamperItems && hamperItems[0] && hamperItems[0].hamperId) || hamperIdRef.current,
                        })
                      }
                      className="flex items-center text-red-500 text-sm hover:underline gap-1 mt-4"
                    >
                      <Trash size={20} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            )}

            {extras.flowers.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mt-10 mb-4">
                  {validHamper ? "Extra Flowers" : "Flowers"}
                </h2>
                {extras.flowers.map((f) => (
                  <div key={f._id} className="flex justify-between items-center border p-4 rounded mb-3">
                    <div className="flex-1 pr-4">
                      <p className="font-semibold">{f.name}</p>
                      <p className="text-sm text-gray-500">${Number(f.price).toFixed(2)}</p>
                    </div>

                    <div className="flex flex-col items-center w-48">
                      <p className="text-base font-medium mb-1">${(Number(f.price) * (f.quantity || 1)).toFixed(2)}</p>

                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => updateFlowerQuantity(f._id, -1)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >−</button>
                        <span className="text-sm font-medium">{f.quantity || 1}</span>
                        <button
                          onClick={() => updateFlowerQuantity(f._id, +1)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >+</button>
                      </div>
                    </div>

                    <button
                      onClick={() => setPendingRemove({ type: "flower", id: f._id })}
                      className="flex items-center text-red-500 text-sm hover:underline gap-1"
                    >
                      <Trash size={20} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {extras.accessories.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mt-10 mb-4">
                  {validHamper ? "Extra Accessories" : "Accessories"}
                </h2>
                {extras.accessories.map((a) => (
                  <div key={a._id} className="flex justify-between items-center border p-4 rounded mb-3">
                    <div className="flex-1 pr-4">
                      <p className="font-semibold">{a.name}</p>
                      <p className="text-sm text-gray-500">${Number(a.price).toFixed(2)}</p>
                    </div>

                    <div className="flex flex-col items-center w-48">
                      <p className="text-base font-medium mb-1">${(Number(a.price) * (a.quantity || 1)).toFixed(2)}</p>

                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => updateAccessoryQuantity(a._id, -1,)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >−</button>
                        <span className="text-sm font-medium">{a.quantity || 1}</span>
                        <button
                          onClick={() => updateAccessoryQuantity(a._id, +1)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >+</button>
                      </div>
                    </div>


                    <button
                      onClick={() => setPendingRemove({ type: "accessory", id: a._id })}
                      className="flex items-center text-red-500 text-sm hover:underline gap-1"
                    >
                      <Trash size={20} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {extras.vips.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mt-10 mb-4">
                  {validHamper ? "Extra VIPs" : "VIPs"}
                </h2>
                {extras.vips.map((a) => (
                  <div
                    key={a._id}
                    className="flex justify-between items-center border p-4 rounded mb-3"
                  >
                    <div className="flex-1 pr-4">
                      <p className="font-semibold">{a.name}</p>
                      <p className="text-sm text-gray-500">
                        ${Number(a.price).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex flex-col items-center w-48">
                      <p className="text-base font-medium mb-1">${(Number(a.price) * (a.quantity || 1)).toFixed(2)}</p>

                      <div className="flex items-center gap-2 mb-2">
                        <button
                          onClick={() => updateVipQuantity(a._id, -1)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >−</button>
                        <span className="text-sm font-medium">{a.quantity || 1}</span>
                        <button
                          onClick={() => updateVipQuantity(a._id, +1)}
                          className="px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                        >+</button>
                      </div>
                    </div>

                    <button
                      onClick={() => setPendingRemove({ type: "vip", id: a._id })}
                      className="flex items-center text-red-500 text-sm hover:underline gap-1"
                    >
                      <Trash size={20} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-6 gap-3">
              {/* Add More Items Button */}
              <button
                onClick={() => {
                  buildModeRef.current = false;
                  navigate("/shop");
                }}
                className="flex items-center bg-gray-600 hover:bg-gray-700 text-white px-5 py-3 rounded-full text-sm font-semibold transition gap-2"
              >
                <Plus size={20} />
                <span>Add More Items</span>
              </button>

              {/* Clear Cart Button */}
              <button
                onClick={() => setShowClearCartModal(true)}
                className="flex items-center bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-full text-sm font-semibold transition gap-2"
              >
                <Trash size={20} />
                <span>Clear Entire Cart</span>
              </button>
            </div>

            {/* Delivery Section */}
            <section data-aos="fade-up">
              <h2 className="text-2xl font-bold text-center mb-6">Delivery Details</h2>
              <div className="bg-white shadow p-6 rounded-xl space-y-6 border border-gray-200">
                <div className="text-gray-600 pt-4 text-xs sm:text-sm mt-4">
                  <strong>Delivery Note:</strong>{" "}
                  Same-day delivery before <span className="font-semibold">{formatCutoffTime(siteSettings?.cutoffTime)}</span> is free within 50 miles of Dallas.
                  Orders after that time attract a <span className="font-semibold">${siteSettings?.sameDayFee || 50}</span> fee if same-day delivery is chosen.
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={useSavedAddress}
                    onChange={() => setUseSavedAddress(!useSavedAddress)}
                    className="w-5 h-5"
                  />
                  <label className="text-sm">Use saved address (if available)</label>
                </div>

                {user && useSavedAddress ? (
                  <div className="space-y-4 text-sm bg-gray-50 p-4 rounded border">
                    <div>
                      <label className="block font-medium mb-1">Choose Saved Address</label>
                      {Array.isArray(user?.addresses) && user.addresses.length > 0 ? (
                        <>
                          <select
                            className="w-full border rounded px-3 py-2"
                            value={selectedSavedAddress?._id || ""}
                            onChange={(e) => {
                              const selected = user?.addresses.find(a => a._id === e.target.value);
                              if (selected) {
                                setSelectedSavedAddress(selected);
                                setDeliveryCity(selected.city || "");
                                setDeliveryAddress(selected.fullAddress || "");
                              }
                            }}
                          >
                            <option value="" disabled>Select an address</option>
                            {user.addresses.map((a) => (
                              <option key={a._id} value={a._id}>
                                {a.label} - {a.city}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <p className="text-gray-500 text-sm mt-2">No saved addresses found in your profile.</p>
                      )}
                    </div>

                    {selectedSavedAddress && (
                      <div className="bg-white border rounded p-3">
                        <p><strong>{selectedSavedAddress.label}</strong></p>
                        <p>{selectedSavedAddress.fullAddress}</p>
                        <p className="text-gray-600">City: {selectedSavedAddress.city}</p>
                      </div>
                    )}
                  </div>
                ) : (

                  <>
                    <div className="relative w-full mb-4">
                      <label className="block mb-1 font-medium text-sm">Delivery City</label>
                      <select
                        value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-sm shadow-sm focus:outline-none"
                        required
                      >
                        <option value="">-- Choose a city --</option>
                        {deliveryCities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-11 -translate-y-1/2 text-gray-400 pointer-events-none">
                        ▼
                      </div>
                    </div>

                    <div className="relative">
                      <label className="block text-sm font-medium mb-1">Enter Address</label>

                      {/* Instructional note if no city is selected */}
                      {!deliveryCity && (
                        <p className="text-sm text-gray-500 mb-2">
                          Please select a city to enable address input.
                        </p>
                      )}

                      <textarea
                        value={deliveryAddress}
                        onChange={handleAddressChange}
                        disabled={!deliveryCity}
                        rows={3}
                        className={`w-full rounded-lg border ${
                          addressError
                            ? "border-red-500"
                            : isAddressValid
                            ? "border-green-500"
                            : "border-gray-300"
                        } px-4 py-2 text-sm resize-none focus:outline-none pr-10 ${
                          !deliveryCity ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        placeholder="Enter full address including street and the city."
                      />

                      {/* Green checkmark when address is valid */}
                      {isAddressValid && !addressError && deliveryCity && (
                        <div className="absolute right-3 top-10 text-green-500">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}

                      {/* Show error if present */}
                      {addressError && (
                        <p className="text-red-500 text-sm mt-1">{addressError}</p>
                      )}
                    </div>
                  </>
                )}

                {isAfterCutoff() &&
                  (useSavedAddress || (deliveryCity && deliveryAddress)) && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-red-600">
                        It's past <span className="font-semibold">{formatCutoffTime(siteSettings?.cutoffTime)}</span>. Would you like same-day delivery for ${siteSettings?.sameDayFee || 50}?
                      </label>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-8 mt-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="sameDayOption"
                            value="yes"
                            checked={wantsSameDay}
                            onChange={() => setWantsSameDay(true)}
                            className="w-5 h-5"
                          />
                          Yes, deliver today (${siteSettings?.sameDayFee})
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="sameDayOption"
                            value="no"
                            checked={!wantsSameDay}
                            onChange={() => setWantsSameDay(false)}
                            className="w-5 h-5"
                          />
                          No, deliver next day (Free)
                        </label>
                      </div>
                    </div>
                )}

                {!isAfterCutoff() && (
                  <div className="text-sm text-green-600">
                    <strong>Free same-day delivery</strong> applies — before <span className="font-semibold">{formatCutoffTime(siteSettings?.cutoffTime)}</span>.
                  </div>
                )}
              </div>
            </section>

            {/* Delivery Note for Driver */}
            <div>
              <label className="block text-sm font-medium mb-1">Delivery Instruction (For dispatch)</label>
              <textarea
                maxLength={150}
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm resize-none focus:outline-none"
                placeholder="Optional note to help the driver find your location"
              />
              <p className="text-xs text-gray-500 mt-1">{150 - deliveryNote.length} characters left</p>
            </div>

            {/* Special Gift Message */}
            <div>
              <label className="block text-sm font-medium mb-1">Special Gift Message</label>
              <textarea
                maxLength={250}
                value={giftMessage}
                onChange={(e) => setGiftMessage(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm resize-none focus:outline-none"
                placeholder="Include a short message to go with the gift"
              />
              <p className="text-xs text-gray-500 mt-1">{250 - giftMessage.length} characters left</p>
            </div>

            {/* Total + Checkout */}
            <div className="bg-white shadow p-6 rounded-xl border border-gray-200 space-y-4 mt-8">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal</span>
                <span>${!isNaN(subtotal) ? subtotal.toFixed(2) : "0.00"}</span>
              </div>
              {sameDayFee > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span>Same-day Delivery</span>
                  <span>+ ${sameDayFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm mb-1">
                <span>Tax</span>
                <span>+ ${!isNaN(taxAmount) ? taxAmount.toFixed(2) : "0.00"}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-blue-700">
                  ${!isNaN(totalAmount) ? totalAmount.toFixed(2) : "0.00"}
                </span>
              </div>
              <button
                onClick={handleProceed}
                disabled={
                  (selectedAccessories.length === 0 && selectedVips.length === 0 && selectedFlowers.length === 0) ||
                  (!useSavedAddress && (!deliveryCity || !deliveryAddress || !isAddressValid)) ||
                  (useSavedAddress && !selectedSavedAddress)
                }
                className={`w-full py-3 rounded-full text-white font-semibold transition ${
                  (selectedAccessories.length === 0 && selectedVips.length === 0 && selectedFlowers.length === 0) ||
                  (!useSavedAddress && (!deliveryCity || !deliveryAddress || !isAddressValid)) ||
                  (useSavedAddress && !selectedSavedAddress)
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={showClearCartModal}
        title="Clear Cart"
        message="Are you sure you want to remove all items from your cart?"
        confirmLabel="Yes, Clear All"
        onConfirm={() => {
          clearCart();
          setShowClearCartModal(false);
        }}
        onCancel={() => setShowClearCartModal(false)}
        onClose={() => setShowClearCartModal(false)}
      />

      <ConfirmModal
        isOpen={!!pendingRemove}
        title="Remove Item"
        message="Are you sure you want to remove this item from your cart?"
        confirmLabel="Yes, Remove"
        onConfirm={() => {
          if (pendingRemove?.type === "flower") {
            removeFlower(pendingRemove.id);
          } else if (pendingRemove?.type === "accessory") {
            removeAccessory(pendingRemove.id);
          } else if (pendingRemove?.type === "vip") {
            removeVip(pendingRemove.id);
          } else if (pendingRemove?.type === "hamper") {
            // ✅ Use the centralized remover by hamperId
            removeHamperById(pendingRemove.id);

            // ✅ Reset hamper-specific UI state after removal
            setHamperType(null);
            setHamperQuantity(1);
            setHamperItems([]);
          }

          // ✅ Always clear pending remove
          setPendingRemove(null);
        }}
        onCancel={() => setPendingRemove(null)}
        onClose={() => setPendingRemove(null)}
      />

      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />

      <div className="pb-24" />
      <Footer />
    </>
  );
}

export default Cart;
